import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { leadsAPI } from '../../services/api';
import { getCountries, getStates, getCities } from '../../data/locationData';
import {
  getAllPhoneCodes,
  getPhoneCodeSelectValueForCountry,
  resolvePhoneCodeSelectValue,
  uniqueCountryForE164,
} from '../../data/phoneCodes';
import {
  getTimezone,
  getCountryFromTimezone,
  getAvailableTimezones,
  timezoneNames,
} from '../../data/timezones';
import { Add as AddIcon } from '@mui/icons-material';
import {
  buildFormStateFromLead,
  serializeLeadFormPayload,
  buildLeadUpdatePatch,
  parsePhoneFromLead,
} from './leadFormPayload';
import { getLeadAgeGroupSelectOptions } from './leadDisplayUtils';
import { LEAD_SOURCE_OPTIONS } from '../../constants/leadSources';
import { ageToAgeGroup } from '../../constants/ageGroups';

// Social Media Input Component
const SocialMediaInput = ({ label, value, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  
  const handleAdd = () => {
    if (inputValue.trim()) {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
    }
  };
  
  const handleRemove = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };
  
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          fullWidth
        />
        <Button size="small" variant="outlined" onClick={handleAdd}>Add</Button>
      </Box>
      {value.map((item, index) => (
        <Chip
          key={index}
          label={item}
          onDelete={() => handleRemove(index)}
          sx={{ mr: 1, mb: 1 }}
        />
      ))}
    </Box>
  );
};

const LeadForm = ({ lead, onSuccess, onCancel, isDialog = false, prefill = null }) => {
  const [formData, setFormData] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: null,
    age: '',
    class_grade: '',
    age_group: '',
    student_type: '',
    class_type_preference: '',
    learning_level: '',
    
    // School Information
    school_name: '',
    school_address: '',
    school_location: '',
    
    // Parent Information
    father_name: '',
    mother_name: '',
    
    // Location Information
    country: '',
    state: '',
    city: '',
    address: '',
    timezone: 'UTC',
    
    // Additional Information
    hobby: '',
    parent_fb_ids: [],
    parent_linkedin_ids: [],
    parent_instagram_ids: [],
    
    // Lead Management
    source: '',
    campaign_name: '',
    fees: '',
    discount: '',
    remarks: '',
    lead_acquired_at: null, // Date when lead was acquired (may be earlier than portal creation)
    support_requirements: '',
    gender: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailWarning, setEmailWarning] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  // State for cascading dropdowns
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [phoneCode, setPhoneCode] = useState(getPhoneCodeSelectValueForCountry('India'));
  const phoneCodeOptions = getAllPhoneCodes();
  /** Snapshot at load time for partial PUT (edit only). */
  const initialPayloadRef = useRef(null);

  const isEditing = !!lead;
  const ageGroupOptions = getLeadAgeGroupSelectOptions(formData.age_group);

  useEffect(() => {
    if (lead && lead.id) {
      const { formData: loaded, phoneCode: loadedPhoneCode } = buildFormStateFromLead(lead);
      setFormData(loaded);
      setPhoneCode(loadedPhoneCode);
      initialPayloadRef.current = serializeLeadFormPayload(loaded, loadedPhoneCode);

      // Populate cascading dropdowns for editing
      if (lead.country) {
        const states = getStates(lead.country);
        setAvailableStates(states);
        if (lead.state) {
          const cities = getCities(lead.country, lead.state);
          setAvailableCities(cities);
        }
      }
    } else {
      initialPayloadRef.current = null;
      const parsed = prefill?.phone ? parsePhoneFromLead(prefill.phone) : null;
      const country = parsed ? (uniqueCountryForE164(prefill.phone) || '') : '';
      setFormData({
        first_name: prefill?.name || '',
        last_name: '',
        email: '',
        phone: parsed?.phoneNumber || '',
        date_of_birth: null,
        age: '',
        class_grade: '',
        age_group: '',
        student_type: '',
        class_type_preference: '',
        learning_level: '',
        school_name: '',
        school_address: '',
        school_location: '',
        father_name: '',
        mother_name: '',
        country,
        state: '',
        city: '',
        address: '',
        timezone: country ? getTimezone(country) : 'UTC',
        hobby: '',
        parent_fb_ids: [],
        parent_linkedin_ids: [],
        parent_instagram_ids: [],
        source: '',
        campaign_name: '',
        fees: '',
        discount: '',
        remarks: '',
        lead_acquired_at: null,
        support_requirements: '',
        gender: '',
      });
      setAvailableStates(country ? getStates(country) : []);
      setAvailableCities([]);
      setPhoneCode(
        parsed
          ? resolvePhoneCodeSelectValue(parsed.phoneCode, country)
          : getPhoneCodeSelectValueForCountry('India')
      );
    }
    setError('');
    setFieldErrors({});
  }, [lead, prefill?.phone, prefill?.name]);


  useEffect(() => {
    let isMounted = true;
    const emailValue = formData.email?.trim();

    if (!emailValue) {
      setEmailWarning('');
      setCheckingEmail(false);
      return () => { isMounted = false; };
    }

    const checkDuplicateEmail = async () => {
      setCheckingEmail(true);
      try {
        const response = await leadsAPI.getAll({ search: emailValue, limit: 1 });
        const leads = response.data?.leads || [];
        const duplicate = leads.some(
          (item) =>
            item.email &&
            item.email.toLowerCase() === emailValue.toLowerCase() &&
            (!isEditing || item.id !== lead?.id)
        );

        if (isMounted) {
          setEmailWarning(
            duplicate
              ? 'Email already exists; this will be another child for that family.'
              : ''
          );
        }
      } catch (err) {
        if (isMounted) {
          setEmailWarning('');
        }
      } finally {
        if (isMounted) {
          setCheckingEmail(false);
        }
      }
    };

    checkDuplicateEmail();

    return () => {
      isMounted = false;
    };
  }, [formData.email, isEditing, lead?.id]);


  const validateField = (field, value) => {
    const errors = {};
    
    switch (field) {
      case 'first_name':
        if (value && value.trim().length > 0 && value.trim().length < 2) {
          errors[field] = 'First name must be at least 2 characters';
        }
        break;
        
      case 'last_name':
        if (value && value.trim().length > 0 && value.trim().length < 2) {
          errors[field] = 'Last name must be at least 2 characters';
        }
        break;
        
      case 'email':
        if (value && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[field] = 'Please enter a valid email address';
        }
        break;
        
      case 'phone':
        if (!value || !value.trim()) {
          errors[field] = 'Phone number is required';
        } else if (!/^[\d\s\-()]+$/.test(value)) {
          errors[field] = 'Please enter a valid phone number';
        }
        break;
        
      case 'date_of_birth':
        if (value) {
          const age = dayjs().diff(value, 'year');
          if (age < 0) {
            errors[field] = 'Date of birth cannot be in the future';
          } else if (age > 120) {
            errors[field] = 'Please enter a valid date of birth';
          }
        }
        break;
        
      case 'country':
        if (!value) {
          errors[field] = 'Country is required';
        }
        break;
        
      case 'fees':
        if (value && (isNaN(value) || parseFloat(value) < 0)) {
          errors[field] = 'Fees must be a positive number';
        }
        break;
        
      case 'discount':
        if (value && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          errors[field] = 'Discount must be between 0 and 100';
        }
        break;

      default:
        break;
    }
    
    return errors;
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // DOB set → always refresh age and age group. DOB cleared → keep existing age.
      if (field === 'date_of_birth' && value) {
        const calculatedAge = dayjs().diff(value, 'year');
        if (calculatedAge >= 0 && calculatedAge <= 120) {
          newData.age = String(calculatedAge);
          const group = ageToAgeGroup(calculatedAge);
          if (group) newData.age_group = group;
        }
      }
      if (field === 'age') {
        const group = ageToAgeGroup(value);
        if (group) newData.age_group = group;
      }

      return newData;
    });

    // Validate field and update errors
    const fieldErrors = validateField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      ...fieldErrors
    }));

    // Clear error for this field if validation passes
    if (!fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Handle cascading dropdowns
    if (field === 'country') {
      const states = getStates(value);
      setAvailableStates(states);
      // Auto-select phone code based on country (unique value so +1 USA ≠ Canada)
      setPhoneCode(getPhoneCodeSelectValueForCountry(value));
      // Auto-populate timezone based on country
      const timezoneValue = getTimezone(value);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        state: '', // Reset state when country changes
        city: '',   // Reset city when country changes
        timezone: timezoneValue // Auto-populate timezone
      }));
      setAvailableCities([]); // Reset cities
    } else if (field === 'state') {
      const cities = getCities(formData.country, value);
      setAvailableCities(cities);
      // Auto-update timezone based on state
      const timezoneValue = getTimezone(formData.country, value);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        city: '', // Reset city when state changes
        timezone: timezoneValue // Update timezone based on state
      }));
    } else if (field === 'timezone') {
      const countryFromTz = getCountryFromTimezone(value);
      if (countryFromTz) {
        setPhoneCode(getPhoneCodeSelectValueForCountry(countryFromTz));
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Lead creation mandatory (frontend only): Source, Campaign name, Lead acquired date
    if (!isEditing) {
      if (!formData.source || !String(formData.source).trim()) errors.source = 'Source is required';
      if (!formData.campaign_name || !String(formData.campaign_name).trim()) errors.campaign_name = formData.source === 'Referral' ? 'Name of Student/Parent is required' : 'Campaign name is required';
      if (!formData.lead_acquired_at) errors.lead_acquired_at = 'Lead acquired date is required';
    }
    
    // Validate only required fields: country and phone
    if (!formData.country) errors.country = 'Country is required';
    if (!formData.phone || !formData.phone.trim()) errors.phone = 'Phone number is required';
    
    // Validate email format if provided
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Validate date of birth if provided
    if (formData.date_of_birth) {
      const age = dayjs().diff(formData.date_of_birth, 'year');
      if (age < 0) {
        errors.date_of_birth = 'Date of birth cannot be in the future';
      } else if (age > 120) {
        errors.date_of_birth = 'Please enter a valid date of birth';
      }
    }
    
    // Validate fees and discount if provided
    if (formData.fees && (isNaN(formData.fees) || parseFloat(formData.fees) < 0)) {
      errors.fees = 'Fees must be a positive number';
    }
    
    if (formData.discount && (isNaN(formData.discount) || parseFloat(formData.discount) < 0 || parseFloat(formData.discount) > 100)) {
      errors.discount = 'Discount must be between 0 and 100';
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    // Validate form before submission
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError('Please correct the errors below');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setFieldErrors({});

      const flat = serializeLeadFormPayload(formData, phoneCode);

      if (isEditing) {
        const initial = initialPayloadRef.current;
        if (!initial) {
          setError('Lead data not loaded; please close and reopen the form.');
          return;
        }
        const patch = buildLeadUpdatePatch(initial, flat);
        if (Object.keys(patch).length === 0) {
          onSuccess();
          return;
        }
        await leadsAPI.update(lead.id, patch);
      } else {
        const leadData = {
          user: {
            email: flat.email,
            password: 'tempPassword123',
            first_name: flat.first_name,
            last_name: flat.last_name,
            phone: flat.phone,
            country: flat.country,
            timezone: flat.timezone,
            state: flat.state,
            city: flat.city,
            address: flat.address,
          },
          student: {
            date_of_birth: flat.date_of_birth,
            age_group: flat.age_group,
            age: flat.age,
            class_grade: flat.class_grade,
            school_name: flat.school_name,
            school_address: flat.school_address,
            school_location: flat.school_location,
            father_name: flat.father_name,
            mother_name: flat.mother_name,
            hobby: flat.hobby,
            parent_fb_ids: flat.parent_fb_ids,
            parent_linkedin_ids: flat.parent_linkedin_ids,
            parent_instagram_ids: flat.parent_instagram_ids,
            fees: flat.fees,
            discount: flat.discount,
            student_type: flat.student_type,
            class_type_preference: flat.class_type_preference,
            learning_level: flat.learning_level,
            support_requirements: flat.support_requirements,
            gender: flat.gender,
            emergency_contact_name: null,
            emergency_contact_phone: null,
            status: 'new',
            source: flat.source,
            campaign_name: flat.campaign_name,
            notes: flat.notes,
            lead_acquired_at: flat.lead_acquired_at,
          },
        };
        try {
          if (flat.phone && flat.first_name) {
            const familyRes = await leadsAPI.familyCheck({ phone: flat.phone, first_name: flat.first_name });
            const family = familyRes.data || {};
            if (family.unique_same_child) {
              const unique = family.unique_same_child;
              const ok = window.confirm(
                `A unique lead already exists for this phone and child (${unique.display_id || unique.first_name || unique.id}). Save as a flagged duplicate (not a second pipeline deal)?`
              );
              if (!ok) return;
              await leadsAPI.create({ ...leadData, confirm_duplicate: true });
              onSuccess();
              return;
            }
            if (family.siblings?.length) {
              const names = family.siblings
                .map((s) => s.first_name || s.display_id)
                .filter(Boolean)
                .join(', ');
              const okSib = window.confirm(
                `This parent phone already has unique lead(s): ${names || 'existing children'}. Save as a sibling (new unique lead)?`
              );
              if (!okSib) return;
            }
          }
          await leadsAPI.create(leadData);
        } catch (createErr) {
          const code = createErr.response?.data?.code;
          if (code === 'DUPLICATE_CHILD') {
            const unique = createErr.response?.data?.unique_lead;
            const ok = window.confirm(
              unique
                ? `A unique lead already exists for this phone and child (${unique.display_id || unique.first_name || unique.id}). Save as a flagged duplicate (not a second pipeline deal)?`
                : 'A unique lead already exists for this phone and child. Save as a flagged duplicate?'
            );
            if (!ok) throw createErr;
            await leadsAPI.create({ ...leadData, confirm_duplicate: true });
          } else {
            throw createErr;
          }
        }
      }

      onSuccess();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onCancel();
    }
  };

  const formContent = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Top Action Button */}
      {!isDialog && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Lead' : 'Add Lead')}
          </Button>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Top Section: Source, Campaign Name, Lead Acquired Date */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Lead Information
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth required={!isEditing} error={!!fieldErrors.source}>
            <InputLabel>Source{!isEditing ? ' *' : ''}</InputLabel>
            <Select
              value={formData.source}
              onChange={(e) => handleChange('source', e.target.value)}
              label={`Source${!isEditing ? ' *' : ''}`}
            >
              {formData.source && !LEAD_SOURCE_OPTIONS.includes(formData.source) && (
                <MenuItem value={formData.source}>{formData.source}</MenuItem>
              )}
              {LEAD_SOURCE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
            {fieldErrors.source && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {fieldErrors.source}
              </Typography>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required={!isEditing}
            label={formData.source === 'Referral' ? 'Name of Student/Parent *' : `Campaign Name${!isEditing ? ' *' : ''}`}
            value={formData.campaign_name}
            onChange={(e) => handleChange('campaign_name', e.target.value)}
            placeholder={formData.source === 'Referral' ? 'Enter student or parent name' : 'Enter campaign name'}
            error={!!fieldErrors.campaign_name}
            helperText={fieldErrors.campaign_name}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <DatePicker
            label={`Lead acquired date${!isEditing ? ' *' : ''}`}
            value={formData.lead_acquired_at}
            onChange={(date) => handleChange('lead_acquired_at', date)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                required: !isEditing,
                error: !!fieldErrors.lead_acquired_at,
                helperText: fieldErrors.lead_acquired_at,
              },
            }}
            maxDate={dayjs()}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            When the lead was actually acquired (can be earlier than today)
          </Typography>
        </Grid>

        {/* Parent Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Parent Information
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Father Name"
            value={formData.father_name}
            onChange={(e) => handleChange('father_name', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Mother Name"
            value={formData.mother_name}
            onChange={(e) => handleChange('mother_name', e.target.value)}
          />
        </Grid>

        {/* Location Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Location Information
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth required error={!!fieldErrors.country}>
            <InputLabel>Country *</InputLabel>
            <Select
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              label="Country *"
            >
              {getCountries().map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.country && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {fieldErrors.country}
              </Typography>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>State</InputLabel>
            <Select
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              label="State"
              disabled={!formData.country}
            >
              <MenuItem value="">{formData.country ? 'Select State' : 'Select Country First'}</MenuItem>
              {availableStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>City</InputLabel>
            <Select
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              label="City"
              disabled={!formData.state}
            >
              <MenuItem value="">{formData.state ? 'Select City' : 'Select State First'}</MenuItem>
              {availableCities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            multiline
            rows={2}
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={formData.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              label="Timezone"
            >
              {getAvailableTimezones().map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {timezoneNames[tz] || tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Phone Country Code</InputLabel>
            <Select
              value={phoneCode}
              onChange={(e) => setPhoneCode(e.target.value)}
              label="Phone Country Code"
            >
              {phoneCodeOptions.map(({ country, code, value }) => (
                <MenuItem key={value} value={value}>
                  {code} ({country})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Phone Number *"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            error={!!fieldErrors.phone}
            helperText={fieldErrors.phone}
            placeholder="Enter phone number"
            required
          />
        </Grid>

        {/* Child Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Child Information
          </Typography>
        </Grid>
        {lead?.display_id && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Lead ID"
              value={lead.display_id}
              disabled
              size="small"
            />
          </Grid>
        )}
        {lead?.display_id && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Lead ID"
              value={lead.display_id}
              disabled
              size="small"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            error={!!fieldErrors.first_name}
            helperText={fieldErrors.first_name}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            error={!!fieldErrors.last_name}
            helperText={fieldErrors.last_name}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />
        </Grid>

        {emailWarning && (
          <Grid item xs={12}>
            <Alert severity="warning">
              {emailWarning}
              {checkingEmail ? ' (checking...)' : ''}
            </Alert>
          </Grid>
        )}
        
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="Date of Birth"
            value={formData.date_of_birth}
            onChange={(date) => handleChange('date_of_birth', date)}
            slotProps={{
              textField: { 
                fullWidth: true,
                error: !!fieldErrors.date_of_birth,
                helperText: fieldErrors.date_of_birth
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Age"
            type="number"
            value={formData.age}
            onChange={(e) => handleChange('age', e.target.value)}
            helperText="Enter age, or pick DOB to auto-calculate age"
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Class/Grade"
            value={formData.class_grade}
            onChange={(e) => handleChange('class_grade', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Age Group</InputLabel>
            <Select
              value={formData.age_group || ''}
              onChange={(e) => handleChange('age_group', e.target.value)}
              label="Age Group"
            >
              <MenuItem value="">Not specified</MenuItem>
              {ageGroupOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Student Type</InputLabel>
            <Select
              value={formData.student_type || ''}
              onChange={(e) => handleChange('student_type', e.target.value)}
              label="Student Type"
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="special_needs">Special Needs</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Class Type Preference</InputLabel>
            <Select
              value={formData.class_type_preference || ''}
              onChange={(e) => handleChange('class_type_preference', e.target.value)}
              label="Class Type Preference"
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="one_on_one">One-on-One</MenuItem>
              <MenuItem value="group">Group</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Learning Level</InputLabel>
            <Select
              value={formData.learning_level || ''}
              onChange={(e) => handleChange('learning_level', e.target.value)}
              label="Learning Level"
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender || ''}
              onChange={(e) => handleChange('gender', e.target.value)}
              label="Gender"
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Support requirements"
            multiline
            rows={2}
            value={formData.support_requirements}
            onChange={(e) => handleChange('support_requirements', e.target.value)}
            placeholder="Special needs, accommodations, etc."
          />
        </Grid>

        {/* School Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            School Information
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="School Name"
            value={formData.school_name}
            onChange={(e) => handleChange('school_name', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="School Address"
            multiline
            rows={2}
            value={formData.school_address}
            onChange={(e) => handleChange('school_address', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="School Location"
            value={formData.school_location}
            onChange={(e) => handleChange('school_location', e.target.value)}
          />
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Additional Information
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Hobby"
            multiline
            rows={2}
            value={formData.hobby}
            onChange={(e) => handleChange('hobby', e.target.value)}
            placeholder="Student's hobbies and interests..."
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <SocialMediaInput
            label="Parent Facebook IDs"
            value={formData.parent_fb_ids}
            onChange={(value) => handleChange('parent_fb_ids', value)}
            placeholder="Enter Facebook ID"
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <SocialMediaInput
            label="Parent LinkedIn IDs"
            value={formData.parent_linkedin_ids}
            onChange={(value) => handleChange('parent_linkedin_ids', value)}
            placeholder="Enter LinkedIn ID"
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <SocialMediaInput
            label="Parent Instagram IDs"
            value={formData.parent_instagram_ids}
            onChange={(value) => handleChange('parent_instagram_ids', value)}
            placeholder="Enter Instagram ID"
          />
        </Grid>

        {/* Lead Management */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Lead Management
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Fees"
            type="number"
            value={formData.fees}
            onChange={(e) => handleChange('fees', e.target.value)}
            error={!!fieldErrors.fees}
            helperText={fieldErrors.fees}
            placeholder="0.00"
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Discount (%)"
            type="number"
            value={formData.discount}
            onChange={(e) => handleChange('discount', e.target.value)}
            error={!!fieldErrors.discount}
            helperText={fieldErrors.discount}
            placeholder="0"
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={3}
            value={formData.remarks}
            onChange={(e) => handleChange('remarks', e.target.value)}
            placeholder="Additional notes..."
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );

  const formActions = (
    <>
      <Button onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {loading ? 'Saving...' : (isEditing ? 'Update Lead' : 'Create Lead')}
      </Button>
    </>
  );

  if (isDialog) {
    return (
      <Dialog open={true} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Lead' : 'Add New Lead'}
        </DialogTitle>
        <DialogContent>
          {formContent}
        </DialogContent>
        <DialogActions>
          {formActions}
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      {formContent}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {formActions}
      </Box>
    </>
  );
};

export default LeadForm;
